import { createContext, useContext, useEffect, useState } from 'react'

/**
 * The console's active-database context.
 *
 * Every page derives from the selection, and the query editor needs it inside
 * callbacks where hooks cannot go, so this is React context rather than URL
 * state alone. The choice persists to localStorage so a reload keeps context.
 */
interface DbContextValue {
  databaseId: string | null
  setDatabaseId: (id: string) => void
}

const DbContext = createContext<DbContextValue | null>(null)

const STORAGE_KEY = 'ms-active-db'

export function DbProvider({ children }: { children: React.ReactNode }) {
  const [databaseId, setDatabaseId] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (!databaseId) return
    try {
      window.localStorage.setItem(STORAGE_KEY, databaseId)
    } catch {
      // Best-effort persistence; the session still works without it.
    }
  }, [databaseId])

  return <DbContext.Provider value={{ databaseId, setDatabaseId }}>{children}</DbContext.Provider>
}

export function useDb(): DbContextValue {
  const context = useContext(DbContext)
  if (!context) throw new Error('useDb must be used inside a <DbProvider>')
  return context
}
